import { defineCommand, defineReceiver } from '@drapejs/invoker';
import { fetchPage } from '@drapejs/core';
import { batch, request, query } from '@distancify/drapejs-litium';
import { gql } from 'graphql-request';
import {
  getProductListingCacheKey,
  getProductListingFacetsCacheKey,
  getFacetsFromQuery,
} from '@/composables/useDynamicProductListing';

import { isPageType } from './shared';

const pageSize = 40;

export const commands = {
  loadMoreProducts: defineCommand<{
    url: string;
    rootCategory: string;
    path: string;
    query: any;
    skip: Number;
    brandValue?: string;
    cacheKey: string;
  }>('loadMoreProducts'),
};

export const schedule = defineReceiver(
  fetchPage,
  async function (command, data) {
    if (
      isPageType(data, 'Category') ||
      (isPageType(data, 'Brand') && data.brandValue)
    ) {
      let facets = getFacets(command, data);

      const commonVariables: any = {
        rootCategory: {
          value: data.categorySystemId || '',
          type: 'String',
        },
        facets: {
          value: facets,
          type: '[SelectedFacetInput]',
        },
        searchPhrase: {
          value: decodeURIComponent(command.query?.search || ''),
          type: 'String',
        },
        brandId: {
          value: (<any>command).brandValue || data.brandValue,
          type: 'String',
        },
      };

      await this.invoke(batch.scheduleQuery, {
        cacheKey: getProductListingFacetsCacheKey(command.path, command.query),
        query: gql`
  productFields(rootCategory: $rootCategory, brandId: $brandId, facets: $facets, excludedFacets: $excludedFacets, searchPhrase: $searchPhrase){
      id
      type
      name
      values {
        count
        label
        value
      }
      selectedValues {
        count
        label
        value
      }
      range {
        min
        max
      }
      selectedRange {
        min
        max
      }
  }
    `,
        variables: {
          ...commonVariables,
          excludedFacets: {
            value: getExcludedFacets(data),
            type: '[String]',
          },
        },
      });

      let takeCount = pageSize;
      const existing:
        | {
            products?: [];
          }
        | undefined = await this.cache.getItem(
        getProductListingCacheKey(command.path, command.query)
      );
      if (
        existing?.products instanceof Array &&
        existing.products.length > pageSize
      ) {
        takeCount = existing.products.length;
      }

      await this.invoke(batch.scheduleQuery, {
        cacheKey: getProductListingCacheKey(command.path, command.query),
        query: gql`
          productSearchV2(rootCategory: $rootCategory, brandId: $brandId, take: $take, skip: $skip, facets: $facets, sort: $sort, searchPhrase: $searchPhrase) {
          ${getProductsSearchFields()}
        }`,
        variables: {
          skip: {
            value: 0,
            type: 'Int',
          },
          take: {
            value: takeCount,
            type: 'Int',
          },
          sort: {
            value: command.query.sort || 'relevance',
            type: 'String',
          },
          ...commonVariables,
        },
      });
    }

    return data;
  }
);

export const receivers = {
  loadMoreProducts: defineReceiver(
    commands.loadMoreProducts,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            query loadMoreProducts(
              $url: String!
              $rootCategory: String
              $brandId: String
              $take: Int
              $skip: Int
              $facets: [SelectedFacetInput]
              $sort: String
              $searchPhrase: String
            ) {
              session(url: $url) {
                productSearchV2(
                  rootCategory: $rootCategory
                  brandId: $brandId
                  take: $take
                  skip: $skip
                  facets: $facets
                  sort: $sort
                  searchPhrase: $searchPhrase
                ) {
                  ${getProductsSearchFields()}
                }
              }
            }
          `
        ),
        {
          url: location.href,
          rootCategory: command.rootCategory,
          brandId: command.brandValue || data.brandValue,
          skip: command.skip,
          take: pageSize,
          facets: getFacets(command, data),
          searchPhrase: decodeURIComponent(command.query?.search || ''),
          sort: command.query.sort || 'relevance',
        }
      );
      const { productSearchV2 } = result.session;
      const cacheKey = getProductListingCacheKey(command.path, command.query);

      const categoryResult = await this.cache.getItem(cacheKey);
      categoryResult.products = [
        ...categoryResult.products,
        ...productSearchV2.products,
      ];
      await this.cache.setItem(cacheKey, categoryResult);
      return categoryResult;
    }
  ),
};

export function getProductsSearchFields(): string {
  return `
    hits
    products {
      articleNumber: id
      url
      fields
      stockStatus
      isMultiPriced
      googleAnalyticsName
      variantsCount
      selectedVariant {
        id
        fields
        url
        isBundle
        price {
          listPrice
          campaignPrice
        }
        priceFlag
      }
    }`;
}

function getExcludedFacets(data: any) {
  if (isPageType(data, 'Brand')) {
    return ['brand'];
  }
  return [];
}

function getFacets(command: any, data: any) {
  let facets = getFacetsFromQuery(command.query);
  if (command.brandValue || data.brandValue) {
    facets = facets.filter((f: any) => f.fieldId != 'brand');
  }

  return facets;
}
